import { NgModule } from '@angular/core';
import { Route, RouterModule, UrlMatchResult, UrlSegment, UrlSegmentGroup } from '@angular/router';
import { DashboardComponent } from '@app/core/components/dashboard/dashboard.component';
import { AboutPageComponent } from '@app/core/pages/about-page/about-page.component';
import { LoginPageComponent } from '@app/core/pages/login-page/login-page.component';
import {
  assignRouteArguments,
  LoginGuard,
  LoginMethodResolverService,
  LogoutGuard,
  LogoutMethodResolverService,
  OptionalUrlMatcher,
  RouteInfo,
} from '@technocom/core';
import { AuthGuard } from '@app/core/guards/auth.guard';
import { Role } from '@app/shared/models/user/role';
import { MobileDownloadPageComponent } from '@app/core/pages/mobile-download-page/mobile-download-page.component';
import { RegistrationPageComponent } from '@app/core/pages/registration-page/registration-page.component';
import { RecoveryPageComponent } from '@app/core/pages/recovery-page/recovery-page.component';

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        {
          path: '',
          component: DashboardComponent,
        },
        {
          path: 'information',
          loadChildren: () =>
            import('@app/information-documents/information-documents.module').then(m => m.InformationDocumentsModule),
        },
        {
          path: 'verification',
          loadChildren: () =>
            import('@app/verification-links/verification-links.module').then(m => m.VerificationLinksModule),
        },
        {
          path: 'news',
          loadChildren: () => import('@app/news/news-module').then(m => m.NewsModule),
        },
        {
          path: 'logs',
          loadChildren: () => import('@app/logs/logs.module').then(m => m.LogsModule),
          canActivate: [AuthGuard],
          data: { requirements: [Role.DATA_ADM] },
        },
        {
          path: 'reports',
          loadChildren: () => import('@app/reports/reports.module').then(m => m.ReportsModule),
          canActivate: [AuthGuard],
          data: { requirements: [Role.OPR] },
        },
        {
          path: 'organizations',
          loadChildren: () =>
            import('@app/assistance-organizations/assistance-organizations.module').then(
              m => m.AssistanceOrganizationsModule,
            ),
        },
        {
          path: 'education',
          data: RouteInfo.of({
            title: 'Обучение мигрантов',
            requirements: [Role.LECTOR],
          }),
          loadChildren: () => import('@app/education/education.module').then(m => m.EducationModule),
          canActivate: [AuthGuard],
        },
        {
          path: 'mobile-app',
          data: RouteInfo.of({
            title: 'Мобильное приложение',
          }),
          component: MobileDownloadPageComponent,
        },
        {
          path: 'about',
          data: RouteInfo.of({
            title: 'О приложении',
          }),
          component: AboutPageComponent,
        },
        {
          path: 'login',
          data: RouteInfo.of({
            title: 'Вход в систему',
          }),
          children: [
            {
              // path: ':method',
              matcher: LoginUrlMatcher,
              component: LoginPageComponent,
              canActivate: [LoginGuard],
              canDeactivate: [LoginGuard],
              resolve: {
                loginBy: LoginMethodResolverService,
              },
            },
          ],
        },
        {
          path: 'registration',
          data: RouteInfo.of({
            title: 'Регистрация',
            authorized: true,
          }),
          component: RegistrationPageComponent,
          canActivate: [AuthGuard],
        },
        {
          path: 'recovery',
          data: RouteInfo.of({
            title: 'Восстановление пароля',
          }),
          component: RecoveryPageComponent,
        },
        {
          path: 'profile',
          data: RouteInfo.of({
            title: 'Профиль',
            requirements: [Role.MIGRANT],
          }),
          loadChildren: () => import('@app/core/profile/profile.module').then(m => m.ProfileModule),
          canActivate: [AuthGuard],
        },
        {
          path: 'logout',
          data: RouteInfo.of({
            title: 'Выход из системы',
          }),
          resolve: {
            logoutBy: LogoutMethodResolverService,
          },
          canActivate: [LogoutGuard],
          canDeactivate: [LogoutGuard],
          children: [],
        },
      ],
      {
        onSameUrlNavigation: 'reload',
      },
    ),
  ],
})
export class CoreRoutingModule {}

export function LoginUrlMatcher(rules: UrlSegment[], group: UrlSegmentGroup, route: Route): UrlMatchResult | null {
  // UrlMatcher может работать с route.path, но ангуляр не даёт задать path и matcher одновременно,
  // поэтому подставляем его в рантайме с помощью assignRouteArguments и радуемся
  return OptionalUrlMatcher(rules, group, assignRouteArguments(route, ':method'));
}
